import { lazy, Suspense, useEffect } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CartDrawer } from "@/components/CartDrawer";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieConsent } from "@/components/CookieConsent";


import { useCartSync } from "@/hooks/useCartSync";
import { initMetaPixel, trackPageView } from "@/lib/meta-pixel";
import { initGtag, gtagPageView } from "@/lib/google-analytics";
import { initTikTokPixel, ttqPageView } from "@/lib/tiktok-pixel";
import { initKlaviyo } from "@/lib/klaviyo";
import { trackPageView as trackPageViewInternal, initEngagementTracking, initErrorTracking } from "@/lib/analytics";
const Index = lazy(() => import("./pages/Index.tsx"));
const ProductPage = lazy(() => import("./pages/ProductPage.tsx"));
const HydrationGuidePage = lazy(() => import("./pages/HydrationGuidePage.tsx"));
const SerumFunnelPage = lazy(() => import("./pages/SerumFunnelPage.tsx"));

const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));

const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
const DataSharingOptOut = lazy(() => import("./pages/DataSharingOptOut.tsx"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage.tsx"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage.tsx"));
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage.tsx"));
const ShippingPage = lazy(() => import("./pages/ShippingPage.tsx"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage.tsx"));
const BlogPage = lazy(() => import("./pages/BlogPage.tsx"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

function AppContent() {
  useCartSync();
  const location = useLocation();

  // Initialize ad pixels immediately — their base code is tiny inline
  // snippets that create a queue. The external scripts load async.
  // Deferring them via requestIdleCallback caused ATC events to be
  // dropped when users clicked before pixels were ready.
  useEffect(() => {
    initMetaPixel(); initGtag(); initTikTokPixel(); initKlaviyo();
  }, []);
  // Start engagement tracking immediately so short sessions still emit
  // active time on visibilitychange/pagehide before the user leaves.
  useEffect(() => {
    initEngagementTracking();
    initErrorTracking();
  }, []);
  useEffect(() => {
    document.documentElement.dataset.oariaBuild = "2026-08-16-auth-assets";
  }, []);
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    trackPageView(); gtagPageView(location.pathname); ttqPageView(); trackPageViewInternal(location.pathname);
  }, [location.pathname]);

  const isAdmin = location.pathname.startsWith("/admin");
  const isFunnel = location.pathname.startsWith("/o/");

  return (
    <>
      {!isAdmin && !isFunnel && <Header />}
      {!isAdmin && !isFunnel && <CartDrawer />}
      <main>
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/product/:handle" element={<ProductPage />} />
            <Route path="/o/hydration-guide" element={<HydrationGuidePage />} />
            <Route path="/o/serum" element={<SerumFunnelPage />} />
            <Route path="/pages/contact-us" element={<ContactPage />} />
            <Route path="/pages/data-sharing-opt-out" element={<DataSharingOptOut />} />
            <Route path="/pages/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/pages/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/pages/track-your-order" element={<TrackOrderPage />} />
            <Route path="/pages/shipping" element={<ShippingPage />} />
            <Route path="/pages/returns" element={<ReturnsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:blogHandle/:articleHandle" element={<BlogPostPage />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/password" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdmin && !isFunnel && <Footer />}
      {!isAdmin && !isFunnel && <CookieConsent />}
    </>
  );
}

/** Inner shell without router — used by both BrowserRouter (client) and StaticRouter (prerender) */
export function AppShell() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AppContent />
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

/** SSR-safe visual shell shared by prerender and client to avoid hydration mismatches. */
export function AppFrame({ children, url }: { children: React.ReactNode; url?: string }) {
  const isFunnel = url?.startsWith("/o/") ?? false;
  return (
    <>
      {!isFunnel && <Header />}
      {!isFunnel && <CartDrawer />}
      <main>{children}</main>
      {!isFunnel && <Footer />}
      {!isFunnel && <CookieConsent />}
    </>
  );
}
